import React from "react";
import { useQuery } from "react-query";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { useTenant } from "../context/TenantContext";
import api from "../services/api";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import ProductGrid from "../components/Product/ProductGrid";
import StorefrontContentBlock from "../components/Layout/StorefrontContentBlock";
import "../styles/storefront-custom-page.css";

const StorefrontPage = () => {
  const { slug } = useParams();
  const { tenant } = useTenant();
  const {
    data: page,
    isLoading,
    error,
  } = useQuery(
    ["storefront-page", tenant?.TenantID, slug],
    async () => {
      const response = await api.get(
        `/api/public/storefront-pages/${encodeURIComponent(slug)}`,
      );
      return response.data.page;
    },
    { enabled: Boolean(tenant?.TenantID && slug), retry: false },
  );
  const components = Array.isArray(page?.components) ? page.components : [];
  const needsProducts = components.some(
    (component) => component.enabled && component.type === "products",
  );
  const needsSlides = components.some(
    (component) => component.enabled && component.type === "slide",
  );
  const { data: products = [], error: productsError } = useQuery(
    ["storefront-page-products", tenant?.TenantID],
    async () => {
      const [physicalResponse, digitalResponse] = await Promise.all([
        api.get(
          "/api/products?productType=physical&displayType=products&limit=100",
        ),
        api.get(
          "/api/products?productType=digital&displayType=products&limit=100",
        ),
      ]);
      return [
        ...(physicalResponse.data?.products || physicalResponse.data || []),
        ...(digitalResponse.data?.products || digitalResponse.data || []),
      ];
    },
    { enabled: Boolean(tenant?.TenantID && needsProducts), staleTime: 60000 },
  );
  const { data: slides = [], error: slidesError } = useQuery(
    ["storefront-page-slides", tenant?.TenantID],
    async () => {
      const response = await api.get("/api/public/slides/active");
      return Array.isArray(response.data?.slides) ? response.data.slides : [];
    },
    { enabled: Boolean(tenant?.TenantID && needsSlides), staleTime: 60000 },
  );

  if (isLoading) {
    return (
      <main className="storefront-custom-page-state">
        <LoadingSpinner />
      </main>
    );
  }

  if (!tenant?.TenantID) {
    return (
      <main className="storefront-custom-page-state">
        <h1>Store unavailable</h1>
        <p>We could not identify this store. Please open the page from its store domain.</p>
      </main>
    );
  }

  if (error || !page) {
    const status = error?.response?.status;
    if (status && status !== 404) {
      return (
        <main className="storefront-custom-page-state">
          <h1>Page temporarily unavailable</h1>
          <p>
            {error.response?.data?.message ||
              "We could not load this page right now. Please try again later."}
          </p>
        </main>
      );
    }
    return (
      <main className="storefront-custom-page-state">
        <h1>Page not found</h1>
        <p>This page may have been unpublished or removed.</p>
      </main>
    );
  }

  return (
    <main className="storefront-custom-page">
      <Helmet>
        <title>{page.seoTitle || page.title} | {tenant?.BusinessName || "Store"}</title>
        {page.seoDescription && (
          <meta name="description" content={page.seoDescription} />
        )}
      </Helmet>
      <article>
        <h1>{page.title}</h1>
        <div
          className="storefront-custom-page-content"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(page.contentHtml || ""),
          }}
        />
        {components
          .filter((component) => component.enabled)
          .map((component) => {
            if (component.type === "products") {
              if (productsError) {
                return (
                  <section
                    className="storefront-page-component storefront-page-component-error"
                    key={component.id}
                  >
                    <h2>{component.title || "Products"}</h2>
                    <p>Products could not be loaded. Please try again later.</p>
                  </section>
                );
              }
              return (
                <section className="storefront-page-component" key={component.id}>
                  {component.title && <h2>{component.title}</h2>}
                  {component.text && <p>{component.text}</p>}
                  <div className="home-products-content storefront-page-products">
                    <ProductGrid
                      products={products.slice(
                        0,
                        [4, 8, 12].includes(component.productCount)
                          ? component.productCount
                          : 8,
                      )}
                      loading={false}
                      gridConfig={{ desktopColumns: 4, mobileColumns: 2 }}
                    />
                  </div>
                </section>
              );
            }

            if (component.type === "slide") {
              if (slidesError) {
                return (
                  <section
                    className="storefront-page-component storefront-page-component-error"
                    key={component.id}
                  >
                    <h2>{component.title || "Featured collection"}</h2>
                    <p>Store slides could not be loaded. Please try again later.</p>
                  </section>
                );
              }
              const slide = slides.find(
                (item) => String(item.SlideID) === String(component.slideId),
              );
              if (!slide) return null;
              const slideLink =
                typeof slide.LinkUrl === "string" &&
                slide.LinkUrl.startsWith("/") &&
                !slide.LinkUrl.startsWith("//")
                  ? slide.LinkUrl
                  : "/shop";
              return (
                <section className="storefront-page-slide" key={component.id}>
                  <img
                    src={slide.ImageData || slide.ImageUrl || slide.image}
                    alt={slide.ImageAltText || slide.Title || component.title}
                    loading="lazy"
                  />
                  <div className="storefront-page-slide-copy">
                    <h2>{component.title || slide.Title || "Featured collection"}</h2>
                    {(component.text || slide.Description) && (
                      <p>{component.text || slide.Description}</p>
                    )}
                    <Link to={slideLink}>Explore collection</Link>
                  </div>
                </section>
              );
            }

            if (component.type === "video") {
              return (
                <StorefrontContentBlock
                  key={component.id}
                  block={{
                    ...component,
                    enabled: true,
                    position: "betweenHeroAndProducts",
                  }}
                />
              );
            }

            if (component.type === "text") {
              return (
                <section className="storefront-page-component" key={component.id}>
                  {component.title && <h2>{component.title}</h2>}
                  {component.text && <p>{component.text}</p>}
                </section>
              );
            }

            return null;
          })}
      </article>
    </main>
  );
};

export default StorefrontPage;
